import {Map, List, fromJS} from 'immutable';
import * as actionTypes from '../../../constants/actionTypes';
import * as filtersConstants from '../../../constants/filters';
import {ActionTypes as FiltersActionTypes} from "./SkuFiltersActions"
import { now } from 'moment';

const initialState = {
  term: '', //header search term
  groups: List(),
  groupsWithValues: List(),
  page: 0,
  totalPages: 0,
  filterGroupUpdatedSuccessful: false,
  expandedFilterGroup: null,
  imagesSeed: (new Date()).getTime(),

  // filter items
  activeFilterGroup: 0,
  filterGroupItemsUpdatedSuccessful: false,
  filterGroupItems: List(),
  expandedFilterGroupItem: null,
  addingNewGroupItem: false,
  additionalImagesPopupVisible: false,
  additionalImagesPopupSelectedId: 0,
  additionalImageData: null,
  additionalImagesToDelete: [],

  // actual filters
  /*

    {
      'materials',
      'color',
      'groupID',...
    }
   */
  filters: Map({
    'materials': null,
    'colors': null
  }),
  allFilterGroups: List() // this is different from groups, as groups can be filtered, while this property will have them all
};

// return the index of a group by its ID if found, -1 otherwise
const __groupIndexByID = (state, filterGroupID) => state.groups.findIndex(g => g.get('id') === filterGroupID);

export const filterGroupByID = (state, filterGroupID) => state.groups.get(__groupIndexByID(state, filterGroupID));

export const createNewFilterGroupItem = (filterGroupID, type = 'Color') => {
  return {
    id: 0,
    isNew: true,
    enabled: true,
    displayText: '',
    value: '',
    type: type,
    description: '',
    icon: '',
    order: 0,
    filterGroupID
  }
}

const __groupItemIndexByID = (state, filterGroupItemID) =>
  state.filterGroupItems.findIndex(i => i.get('id') === filterGroupItemID);

const adjustResult = (state, data) => {
  return {...state, groups: fromJS(data.rows), totalPages: data.totalPages, imagesSeed: (new Date()).getTime()};
};

const expandGroup = (state, filterGroupID) => {
  let id = state.expandedFilterGroup && state.expandedFilterGroup.get('id') === filterGroupID ? 0 : filterGroupID;
  let expandedFilterGroup = null;
  if (id) {
    expandedFilterGroup = state.groups.get(__groupIndexByID(state, id));
  }

  return {...state, expandedFilterGroup};
}

const updateFilterGroupProp = (state, data) => {
  const {filterGroupID, propName, value} = data;
  const idx = __groupIndexByID(state, filterGroupID);
  const groups = state.groups.setIn([idx, propName], value);
  return {...state, groups};
}

const updateExpandedFilterGroupProp = (state, data) => {
  const {propName, value} = data;
  const expandedFilterGroup = state.expandedFilterGroup ? state.expandedFilterGroup.set(propName, value) : state.expandedFilterGroup;
  return {...state, expandedFilterGroup};
}

const setExpandedFilterGroupIcon = (state, data) => {
  const expandedFilterGroup = state.expandedFilterGroup.set('iconBlob', data);
  return {...state, expandedFilterGroup};
}

const tryInitializeNewFilterGroup = state => {
  if (state.groups.size > 0 && state.groups.getIn([0, 'isNew'])) return state; // already adding a new group

  const newGroup = fromJS({
    id: 0,
    isNew: true,
    name: '',
    description: '',
    visualType: filtersConstants.FILTER_GROUP_VISUAL_TYPE_ColorsWithColumns,
    icon: '',
    enabled: true
  });

  return {...state, groups: state.groups.insert(0, newGroup), expandedFilterGroup: newGroup}
};

const cancelEditFilterGroup = (state, filterGroupID) => {
  const idx = __groupIndexByID(state, filterGroupID);
  const group = state.groups.get(idx);
  if (group.get('isNew')) {
    return {...state, groups: state.groups.remove(idx), expandedFilterGroup: null};
  }

  return {...state, expandedFilterGroup: null};
}

const adjustFilterGroupItemsResult = (state, data) => {
  let filterGroupItems = fromJS(data);
  let expandedFilterGroupItem = null;
  if (!filterGroupItems.size) {
    expandedFilterGroupItem = fromJS(createNewFilterGroupItem(state.activeFilterGroup.get('id'), 'Color'));
    filterGroupItems = filterGroupItems.push(expandedFilterGroupItem);
  }

  return {...state,
    filterGroupItems,
    expandedFilterGroupItem
  };
}

const expandFilterGroupItem = (state, filterGroupItemID) => {
  const id = state.expandedFilterGroupItem &&
  state.expandedFilterGroupItem.get('id') === filterGroupItemID ? 0 : filterGroupItemID;
  let expandedFilterGroupItem = null;
  if (id) {
    expandedFilterGroupItem = state.filterGroupItems.get(__groupItemIndexByID(state, id));
  }

  return {...state, expandedFilterGroupItem};
}

const tryInitializeNewFilterGroupItem = (state, filterGroupID) => {
  if (state.filterGroupItems.size > 0 && state.filterGroupItems.getIn([0, 'isNew']))
    return state; // already adding a new group item

  let defType = 'Color';
  if (state.filterGroupItems.size > 0) {
    defType = state.filterGroupItems.getIn([0, 'type']);
  }

  const newGroupItem = fromJS(createNewFilterGroupItem(filterGroupID, defType));

  return {
    ...state, filterGroupItems: state.filterGroupItems.insert(0, newGroupItem),
    expandedFilterGroupItem: newGroupItem, addingNewGroupItem: true
  }
};


const updateExpandedFilterGroupItemProp = (state, data) => {
  const {propName, value} = data;
  const expandedFilterGroupItem = state.expandedFilterGroupItem.set(propName, value);
  return {...state, expandedFilterGroupItem};
}

const cancelFilterGroupItemEdit = (state, filterGroupItemID) => {
  const idx = __groupItemIndexByID(state, filterGroupItemID);
  const groupItem = state.filterGroupItems.get(idx);
  if (groupItem.get('isNew')) {
    return {
      ...state, filterGroupItems: state.filterGroupItems.remove(idx), expandedFilterGroupItem: null,
      addingNewGroupItem: false
    };
  }

  return {...state, expandedFilterGroupItem: null, addingNewGroupItem: false};
}

const setExpandedFilterGroupItemIcon = (state, data) => {
  const expandedFilterGroupItem = state.expandedFilterGroupItem.set('iconBlob', data);
  return {...state, expandedFilterGroupItem};
}

const setFilterValuesByType = (state, {payload}) => {
  const {type, filterGroup} = payload;
  let {filters} = state;
  filters = filters.set(type, fromJS(filterGroup));
  return {...state, filters};
}

export default (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.SKU_FILTERS_LOADING:
      return {...state, term: action.payload, filterGroupUpdatedSuccessful: false};
    case actionTypes.SKU_FILTERS_LOADED:
      return adjustResult(state, action.payload);
    case actionTypes.SKU_FILTERS_GROUP_ENABLE_UPDATING:
      return {...state, filterGroupUpdatedSuccessful: false};
    case actionTypes.SKU_FILTERS_GROUP_ENABLE_UPDATED:
      return {...state, filterGroupUpdatedSuccessful: true};
    case actionTypes.SKU_FILTERS_GROUP_EXPAND:
      return expandGroup(state, +action.payload);
    case actionTypes.SKU_FILTERS_GROUP_UPDATE_PROP:
      return updateFilterGroupProp(state, action.payload);
    case actionTypes.SKU_FILTERS_GROUP_EXPANDED_UPDATE_PROP:
      return updateExpandedFilterGroupProp(state, action.payload);
    case actionTypes.POPUP_FILTER_GROUP_VISUAL_STYLE_SELECTED:
      return updateExpandedFilterGroupProp(state, {propName: 'visualType', value: action.payload});
    case actionTypes.SKU_FILTERS_GROUP_SET_ACTIVE_GROUP_ICON:
      return setExpandedFilterGroupIcon(state, action.payload);
    case actionTypes.SKU_FILTERS_GROUP_SAVING:
      return {...state, filterGroupUpdatedSuccessful: false, expandedFilterGroup: null};
    case actionTypes.SKU_FILTERS_GROUP_SAVED:
      return {...state, filterGroupUpdatedSuccessful: true};
    case actionTypes.SKU_FILTERS_GROUP_NEW:
      return tryInitializeNewFilterGroup(state);
    case actionTypes.SKU_FILTER_GROUP_CANCEL:
      return cancelEditFilterGroup(state, +action.payload);
    case actionTypes.SKU_FILTER_GROUP_DELETING:
      return {...state, filterGroupUpdatedSuccessful: false};
    case actionTypes.SKU_FILTER_GROUP_DELETED:
      return {...state, filterGroupUpdatedSuccessful: true};
    case actionTypes.SKU_FILTERS_ITEMS_LOADING:
      return {
        ...state,
        activeFilterGroup: filterGroupByID(state, action.payload),
        filterGroupItemsUpdatedSuccessful: false,
        filterGroupItems: List()
      };
    case actionTypes.SKU_FILTERS_ITEMS_LOADED:
      return adjustFilterGroupItemsResult(state, action.payload);
    case actionTypes.SKU_FILTERS_GROUP_ITEM_ENABLE_UPDATING:
    case actionTypes.SKU_FILTERS_GROUP_ITEM_ORDER_UPDATING:
      return {...state, filterGroupItemsUpdatedSuccessful: false};
    case actionTypes.SKU_FILTERS_GROUP_ITEM_ENABLE_UPDATED:
    case actionTypes.SKU_FILTERS_GROUP_ITEM_ORDER_UPDATED:
      return {...state, filterGroupItemsUpdatedSuccessful: true};
    case actionTypes.SKU_FILTERS_GROUP_ITEM_EXPAND:
      return expandFilterGroupItem(state, +action.payload);
    case actionTypes.SKU_FILTERS_GROUP_ITEM_EXPANDED_UPDATE_PROP:
      return updateExpandedFilterGroupItemProp(state, action.payload);
    case actionTypes.SKU_FILTER_GROUP_ITEM_CANCEL:
      return cancelFilterGroupItemEdit(state, +action.payload);
    case actionTypes.SKU_FILTERS_GROUP_SET_ACTIVE_GROUP_ITEM_ICON:
      return setExpandedFilterGroupItemIcon(state, action.payload);
    case actionTypes.SKU_FILTERS_GROUP_ITEM_NEW:
      return tryInitializeNewFilterGroupItem(state, action.payload);
    case actionTypes.SKU_FILTERS_GROUP_ITEM_SAVING:
      return {...state, filterGroupItemsUpdatedSuccessful: false, expandedFilterGroupItem: null};
    case actionTypes.SKU_FILTERS_GROUP_ITEM_SAVED:
      return {...state, filterGroupItemsUpdatedSuccessful: true, addingNewGroupItem: false};
    case actionTypes.SKU_FILTER_GROUP_ITEM_DELETING:
      return {...state, filterGroupItemsUpdatedSuccessful: false, expandedFilterGroupItem: null};
    case actionTypes.SKU_FILTER_GROUP_ITEM_DELETED:
      return {...state, filterGroupItemsUpdatedSuccessful: true, addingNewGroupItem: false};
    case FiltersActionTypes.SKU_FILTERS_STONE_FILTERS_BY_TYPE_LOADED:
      return setFilterValuesByType(state, action);
    case FiltersActionTypes.SKU_FILTERS_GET_ALL_GROUPS_LOADED:
      return {...state, allFilterGroups: fromJS(action.payload)};
    case actionTypes.SKU_FILTER_GROUP_ITEM_LOCK_TOGGLE:
      let giId = action.payload;
      let index = __groupItemIndexByID(state, giId);
      let newState = {...state};
      let existingValue = newState.filterGroupItems.getIn([index, 'lockedInWizard']);
      newState.filterGroupItems = newState.filterGroupItems.map(item => {
        return item.get("id") == giId ? item.set("lockedInWizard", !existingValue) : item;
      });
      return newState;
    case actionTypes.SKU_FILTER_GROUP_ITEM_ADDITIONAL_POPUP_SHOW:
      return {
        ...state,
        additionalImagesPopupVisible: action.payload.show,
        additionalImagesPopupSelectedId: action.payload.id,
        additionalImageData: action.payload.additionalImages
      }
    case actionTypes.SKU_FILTER_GROUP_LOAD_WITH_VALUES:
      return {...state, groupsWithValues: new List(action.payload.filterGroups)};
    case actionTypes.SKU_FILTER_GROUP_ITEM_DELETE_ADDITIONAL_IMAGE:
      newState = {...state};
      var imageIndex = newState.additionalImageData.indexOf(action.payload);
      newState.additionalImageData.splice(imageIndex, 1);
      newState.additionalImageData = [...newState.additionalImageData];
      newState.additionalImagesToDelete = [...newState.additionalImagesToDelete || []];
      newState.additionalImagesToDelete.push(action.payload);
      return newState;
    case actionTypes.SKU_FILTER_GROUP_ITEM_ADD_ADDITIONAL_IMAGE:
      newState = {...state};
      newState.additionalImageData.push({
        id: 0,
        filterGroupValue_id: state.additionalImagesPopupSelectedId,
        imageUrl: action.payload,
        filterValueIds: []
      });
      newState.additionalImageData = [...newState.additionalImageData];
      return newState;
    case actionTypes.SKU_FILTER_GROUP_ITEM_SET_ADDITIONAL_IMAGE_FILTERS:
      newState = {...state};
      index = newState.additionalImageData.findIndex(_ => _ === action.payload.image);
      newState.additionalImageData[index].filterValueIds = [...action.payload.filters];
      return newState;
    default:
      return state;
  }
};

export const getFiltersFromGroupByType = (state, type) => {
  const {filters} = state;
  const filterGroup = filters.get(type);
  return filterGroup ? filterGroup.toJS() : null;
}
